// @ts-nocheck

import { __ } from '../i18n/l10n';
import { type StateType } from '../states';
import { type CategoryState } from '../states/entities/categories';
import { type SportState } from '../states/entities/sports';
import { type TournamentState } from '../states/entities/tournaments';
import { type SportsMenuItem } from '../states/sportsmenu';
import { CategoryIds } from '../types/categories';
import { type Selector } from '../types/selectors';
import { SportIds } from '../types/sports';
import { TopCompetitionLevels } from '../types/tournaments';
import { createResultEqualSelector } from '../utils/reselect';

import { getSportIdFromProps } from './_propsGetters_';
import { selectEntitiesCategories } from './entities/categories/entitiesCategoriesPrimary.selector';
import { selectEntitiesSportIds } from './entities/sportIds/entitiesSportIdsPrimary.selector';
import { selectEntitiesSports, selectEntitiesSportsEntity } from './entities/sports/entitiesSportsPrimary.selector';
import { selectEntitiesTournaments } from './entities/tournaments/entitiesTournamentsPrimary.selector';

const emptyArr = [];

export const SportsMenuSportIdsWithCountryTitle = [
  SportIds.SOCCER,
  SportIds.BASKETBALL,
  SportIds.BASEBALL,
  SportIds.ICEHOCKEY,
  SportIds.HANDBALL,
  SportIds.RUGBY,
  SportIds.FOOTBALL,
  SportIds.VOLLEYBALL,
  SportIds.RUGBY_LEAGUE_OLD,
  SportIds.RUGBY_7S_OLD,
  SportIds.RUGBY_7S,
  SportIds.RUGBY_LEAGUE,
  SportIds.FUTSAL,
  SportIds.AUSSIE_RULES,
];

export const SportsMenuSportIdsWithCompetitions = [
  SportIds.BOWLS,
  SportIds.BOXING,
  SportIds.CURLING,
  SportIds.CYCLING,
  SportIds.DARTS,
  SportIds.GOLF,
  SportIds.SNOOKER,
  SportIds.SQUASH,
  SportIds.FORMULA_1,
  SportIds.ALPINE,
  SportIds.BIATHLON,
  SportIds.BOBSLEIGH,
  SportIds.CROSS_COUNTRY,
  SportIds.NORDIC_COMBINED,
  SportIds.SKI_JUMPING,
  SportIds.SNOWBOARD,
  SportIds.SPEED_SKATING,
  SportIds.LUGE,
  SportIds.FREESTYLE_SKIING,
  SportIds.SKELETON,
  SportIds.SHORT_TRACK_SPEED_SKATING,
  SportIds.SOCCER_7,
];

export const getSportsMenuTitle = (sportId?: number): string => {
  if (!sportId) {
    return __('Sports');
  }
  if (SportsMenuSportIdsWithCountryTitle.indexOf(parseInt(sportId)) !== -1) {
    return __('Countries');
  }

  return __('Competitions');
};

const selectCategoriesBySportId: Selector<Array<CategoryState> | undefined> = createResultEqualSelector(
  [selectEntitiesSportsEntity, selectEntitiesCategories],
  (sportBySportId, categories) => {
    return sportBySportId?.categories?.map((categoryId) => categories[categoryId]);
  },
);

type TournamentsBySportIdType = Array<
  TournamentState & {
    sportId?: number;
    label: string;
    flagId?: string;
    categoryFlagId?: string;
    categoryName?: string;
  }
>;

export const selectSportsMenuTournamentsBySportId: Selector<TournamentsBySportIdType | undefined> =
  createResultEqualSelector(
    [selectCategoriesBySportId, selectEntitiesTournaments, getSportIdFromProps],
    (categoriesBySportId, tournaments, sportId) =>
      categoriesBySportId
        ?.map((category) => {
          if (!category) {
            return emptyArr;
          }
          const flag = category.flag;
          return category.tournaments.map((tournamentId) => {
            const tt = tournaments[tournamentId];
            let label = category.categoryName ? `${category.categoryName} - ` : '';
            label += tt?.tournamentName ?? '';
            label = tt?.topName ?? label;

            return {
              ...tt,
              categoryId: category.categoryId,
              sportId,
              label,
              flagId: tt?.flag ? tt.flag : flag,
              categoryFlagId: category.flag,
              categoryName: category.categoryName,
            };
          });
        })
        .reduce((acc, curr) => [...acc, ...curr], []),
  );

type SportBySportIdsType = Array<SportState>;

export const selectSportMenuSportBySportIds: Selector<SportBySportIdsType | null | undefined> =
  createResultEqualSelector(
    [selectEntitiesSports, selectEntitiesSportIds, getSportIdFromProps],
    (sports, sportIds, propSportId) => (propSportId ? null : sportIds.map((sportId) => sports[sportId])),
  );

type CategoriesBySportIdsType = Array<CategoryState & { sportId?: number }>;

const selectCategoriesBySportIds: Selector<CategoriesBySportIdsType | undefined> = createResultEqualSelector(
  [selectSportMenuSportBySportIds, selectEntitiesCategories],
  (sportsBySportIds, categories) =>
    sportsBySportIds
      ?.map((sport) =>
        sport?.categories?.map((categoryId) => ({
          ...categories[categoryId],
          sportId: sport.sportId,
        })),
      )
      .filter((k) => k)
      .reduce((acc, curr) => [...acc, ...curr], []),
);

export const selectTournamentsBySportIds: Selector<TournamentsBySportIdType | undefined> = createResultEqualSelector(
  [selectCategoriesBySportIds, selectEntitiesTournaments],
  (categoriesBySportIds, tournaments) =>
    categoriesBySportIds
      ?.map((category) => {
        if (!category) {
          return [];
        }
        const flag = category.flag;
        return category.tournaments
          ?.map((tournamentId) => tournaments[tournamentId])
          .filter((k) => k)
          .map((tt) => {
            let label = category?.categoryName ? `${category?.categoryName} - ` : '';
            label += tt?.tournamentName ?? '';
            label = tt?.topName ?? label;

            return {
              ...tt,
              categoryId: category.categoryId,
              sportId: category.sportId,
              label,
              flagId: tt?.flag ? tt.flag : flag,
              categoryFlagId: category.flag,
              categoryName: category.categoryName,
            };
          });
      })
      .reduce((acc, curr) => (curr ? [...acc, ...curr] : acc), []),
);

const selectTournamentsBySport: Selector<TournamentsBySportIdType | undefined> = createResultEqualSelector(
  [selectSportsMenuTournamentsBySportId, selectTournamentsBySportIds],
  (tournamentsBySportId, tournamentBySportIds) => tournamentsBySportId ?? tournamentBySportIds,
);

const selectTournamentsBySportByLevel: Selector<TournamentsBySportIdType | undefined> = createResultEqualSelector(
  [selectTournamentsBySport, getSportIdFromProps],
  (tournamentsBySport, sportId) =>
    tournamentsBySport?.filter((tt) => {
      return sportId ? !!tt?.topLevel : tt?.topLevel === TopCompetitionLevels.SPORT;
    }),
);

// no-memoize
export const selectSportsMenuMultiFootCategory: Selector<CategoryState | undefined> = (state: StateType) =>
  selectEntitiesCategories(state)?.[CategoryIds.MULTIFOOT];

export const selectSportsMenuMainTopCompetitions: Selector<Array<SportsMenuItem>> = createResultEqualSelector(
  [selectTournamentsBySportByLevel, selectSportsMenuMultiFootCategory, getSportIdFromProps],
  (tournamentsBySportWithLevel, multifootCategory, sportId) => {
    const tournamentsMainTop = tournamentsBySportWithLevel?.map(
      ({ categoryId, tournamentId, flagId, sportId, label }) => {
        return {
          sportId,
          flagId,
          label,
          to: `/sports/${sportId}/${categoryId}/${tournamentId}`,
          categoryId,
        };
      },
    );

    const multiFootMainTop = [];
    if (sportId === SportIds.SOCCER && multifootCategory) {
      multiFootMainTop.push({
        flagId: multifootCategory.flag,
        label: multifootCategory.categoryName,
        to: `/sports/${SportIds.SOCCER}/${multifootCategory.categoryId}`,
        categoryId: multifootCategory.categoryId,
      });
    }

    return [...tournamentsMainTop, ...multiFootMainTop];
  },
);
